import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { SupportRequestsRepository } from "@/application/support/support_repository";
import type { SupportTicketRow } from "@/application/support/types";
import { topicLabel } from "@/application/support/types";
import { SupportStatusBadge } from "./SupportStatusBadge";
import { SupportDetailSheet } from "./SupportDetailSheet";

const repo = new SupportRequestsRepository();

function formatDate(value: string | null): string {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "—" : date.toLocaleDateString();
}

export function MerchantSupportTab({
  businessId,
  limit,
  onCountChange,
}: {
  businessId: string;
  /** Caps the rows shown. The caller links to the full list. */
  limit?: number;
  /**
   * How many tickets this merchant has, once known. Lets the caller drop a
   * "View all" that would lead to an empty page.
   */
  onCountChange?: (count: number) => void;
}) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tickets, setTickets] = useState<SupportTicketRow[]>([]);
  const [openTicketId, setOpenTicketId] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const rows = await repo.listByBusiness(businessId);
        if (!cancelled) {
          setTickets(rows);
          onCountChange?.(rows.length);
        }
      } catch (err) {
        if (!cancelled) {
          setError(
            err instanceof Error
              ? err.message
              : "Failed to load support requests",
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [businessId, reloadKey, onCountChange]);

  if (loading) {
    return (
      <div className="flex min-h-[160px] items-center justify-center">
        <Loader2 className="text-subtle-foreground h-6 w-6 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-card rounded-lg border px-4 py-8 text-center text-sm text-red-500">
        {error}
      </div>
    );
  }

  if (tickets.length === 0) {
    return (
      <div className="bg-card text-muted-foreground rounded-lg border p-8 text-center">
        <p className="text-sm">No support requests from this merchant.</p>
      </div>
    );
  }

  const shown = limit ? tickets.slice(0, limit) : tickets;

  return (
    <div className="bg-card overflow-hidden rounded-lg border">
      <table className="divide-border min-w-full divide-y text-sm">
        <thead className="bg-muted">
          <tr className="text-muted-foreground text-left text-xs font-semibold tracking-wide uppercase">
            <th className="px-4 py-2.5">Reference</th>
            <th className="px-4 py-2.5">Topic</th>
            <th className="px-4 py-2.5">Status</th>
            <th className="px-4 py-2.5">Created</th>
          </tr>
        </thead>
        <tbody className="divide-border divide-y">
          {shown.map((t) => (
            <tr
              key={t.id}
              onClick={() => setOpenTicketId(t.id)}
              className="hover:bg-muted cursor-pointer"
            >
              <td className="text-foreground px-4 py-2.5 font-mono font-medium">
                {t.reference}
              </td>
              <td className="text-foreground px-4 py-2.5">
                {topicLabel(t.topic)}
              </td>
              <td className="px-4 py-2.5">
                <SupportStatusBadge status={t.status} />
              </td>
              <td className="text-muted-foreground px-4 py-2.5">
                {formatDate(t.createdAt)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {openTicketId && (
        <SupportDetailSheet
          ticketId={openTicketId}
          onClose={() => setOpenTicketId(null)}
          onRetried={() => setReloadKey((key) => key + 1)}
        />
      )}
    </div>
  );
}
